import { useAtomValue } from "jotai";

import { currentProfileAtom } from "../../../../../features/Club/atoms";
import { useUpdateEventSignUp } from "../../../hooks/useUpdateEventSignUp";
import { EventSignUpRow } from "../../../types";
import { resolveRemovalPayer } from "./resolveRemovalPayer";

type handleRemoveCurrentUserProps = {
  id: string;
  eventSignUpRow: EventSignUpRow;
};

export const useRemoveCurrentUserFromEvent = (successCallback: () => void) => {
  const [updateEventSignUp, isUpdatingEventSignup] =
    useUpdateEventSignUp(successCallback);

  const currentProfile = useAtomValue(currentProfileAtom);

  const handleRemoveCurrentUser = ({
    id,
    eventSignUpRow,
  }: handleRemoveCurrentUserProps) => {
    const removedMemberId = currentProfile?.memberId;

    const filteredRegistrations = eventSignUpRow.registrations.filter(
      (r) => r.registrant.playerId !== removedMemberId
    );
    const newMemberRegistrations = filteredRegistrations.filter(
      (r) => r.type === "MEMBER"
    );

    // The owner inherits only the charges the leaving member was covering.
    // Prefer the explicit signup owner, including proxy owners who are not registered.
    const remainingMembers = newMemberRegistrations.filter(
      (nmr) =>
        !!nmr?.registrant?.playerId &&
        nmr.registrant.playerId !== removedMemberId
    );
    const ownerMemberId =
      eventSignUpRow.ownerClient?.type === "MEMBER"
        ? eventSignUpRow.ownerClient.id
        : remainingMembers[0]?.registrant.playerId ?? null;

    if (ownerMemberId) {
      // Preserve each remaining registration's existing payer. Only registrations
      // the leaving member was paying for are reassigned to the owner. The fallback
      // maintains the mutation's required non-null payer invariant.
      const resolvePayingMemberId = (
        registration: EventSignUpRow["registrations"][number]
      ) =>
        resolveRemovalPayer(
          registration.payingMemberId,
          removedMemberId,
          ownerMemberId
        ) ?? ownerMemberId;

      const input = {
        eventId: id,
        notifyMember: true,

        eventSignUp: {
          memberRegistrations: newMemberRegistrations.flatMap(
            (registration, i) => {
              return registration.type === "MEMBER"
                ? [
                    {
                      id: registration.id,
                      index: registration.index ?? i,
                      payingMemberId: resolvePayingMemberId(registration),
                      playerId: registration.registrant.playerId ?? null,
                      formResponses: registration.formResponses,
                      primaryRegistrationChargeTypeId:
                        registration.primaryRegistrationChargeTypeId,
                    },
                  ].filter((r) => r.playerId !== removedMemberId)
                : [];
            }
          ),
          guestRegistrations: filteredRegistrations.flatMap(
            (registration, i) => {
              return registration.type === "GUEST"
                ? [
                    {
                      id: registration.id,
                      index: registration.index ?? i,
                      payingMemberId: resolvePayingMemberId(registration),
                      playerId: registration.registrant.playerId ?? null,
                      formResponses: registration.formResponses,
                      primaryRegistrationChargeTypeId:
                        registration.primaryRegistrationChargeTypeId,
                    },
                  ]
                : [];
            }
          ),
          newGuestRegistrations: filteredRegistrations.flatMap(
            (registration, i) => {
              return registration.type === "New Guest"
                ? [
                    {
                      id: registration.id ? registration.id : undefined,
                      index: registration.index ?? i,
                      payingMemberId: resolvePayingMemberId(registration),
                      name: `${registration.registrant.firstName} ${registration.registrant.lastName}`,
                      formResponses: registration.formResponses,
                      email: registration.registrant.email,
                      primaryRegistrationChargeTypeId:
                        registration.primaryRegistrationChargeTypeId,
                    },
                  ]
                : [];
            }
          ),
        },
      };

      if (eventSignUpRow.id) {
        const { eventId, ...rest } = input;
        const updateInput = { ...rest, eventSignUpId: eventSignUpRow.id };
        updateEventSignUp(updateInput);
      }
    }

    return null;
  };

  return [handleRemoveCurrentUser, isUpdatingEventSignup] as const;
};
